/**
 * Classify Holding Modal
 * Modal for classifying a single holding's asset class
 */
import { useState, useEffect } from 'react';
import { useClassifyHolding, useHoldings } from '@/hooks/useAllocation';
import Button from '@/components/ui/Button';
import {
  ASSET_CLASS_DISPLAY_NAMES,
  type AssetClass,
  type ClassifyHoldingRequest,
} from '@/types/allocation';

interface ClassifyHoldingModalProps {
  isOpen: boolean;
  holdingId: string | null;
  onClose: () => void;
}

export default function ClassifyHoldingModal({
  isOpen,
  holdingId,
  onClose,
}: ClassifyHoldingModalProps) {
  const { data: holdingsData } = useHoldings();
  const { mutate: classifyHolding, isPending } = useClassifyHolding();

  // Form state
  const [assetClass, setAssetClass] = useState<AssetClass>('other');
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Find the holding being classified
  const holding = holdingsData?.holdings.find((h) => h.holdingId === holdingId);

  // Populate form when holding changes
  useEffect(() => {
    if (holding) {
      setAssetClass(holding.assetClass);
      setOverrideReason('');
    }
  }, [holding]);

  if (!isOpen || !holdingId) return null;

  const handleSubmit = () => {
    setError(null);
    const data: ClassifyHoldingRequest = {
      assetClass,
      overrideReason: overrideReason.trim() || undefined,
    };
    classifyHolding(
      { holdingId, data },
      {
        onSuccess: () => handleClose(),
        onError: (err: Error & { detail?: string }) => {
          // Show the error message from the backend
          const message = err.detail || err.message || 'Failed to classify holding';
          setError(message);
        },
      }
    );
  };

  const handleClose = () => {
    setAssetClass('other');
    setOverrideReason('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform bg-white rounded-lg shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Classify Holding
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Holding info */}
            {holding && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">
                  {holding.securityName}
                </p>
                {holding.ticker && (
                  <p className="text-xs text-gray-500">{holding.ticker}</p>
                )}
                <p className="mt-1 text-sm text-gray-600">
                  ${formatNumber(holding.currentValue)}
                </p>
                <p className="text-xs text-gray-500">
                  Account: {holding.accountName}
                </p>
              </div>
            )}

            {/* Asset class selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset Class
              </label>
              <select
                value={assetClass}
                onChange={(e) => setAssetClass(e.target.value as AssetClass)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {Object.entries(ASSET_CLASS_DISPLAY_NAMES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Override reason (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Why are you classifying this holding as this asset class?"
                rows={3}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Adding a reason helps you remember why you classified it this way
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={isPending}
            >
              Save Classification
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function formatNumber(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}
